// ============================================================
// App.jsx — theVisaCo eSIM Platform (Redesigned)
// ============================================================

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  SITE_CONFIG, CURRENCIES, PAYMENT_METHODS,
  BANKS, convertPrice, customRound, getPlanTypeLabel,
  UI_CONFIG, PAYMENT_CONVENIENCE_FEES, TAX_RATE, TERMS_AND_CONDITIONS,
  generateReceiptHTML, EXCHANGE_RATES, getCountryName,
  FEATURES, MAX_REWARD_DISCOUNT_PCT, WHATSAPP_COUNTRY_CODES,
  PLAN_TYPE_FILTERS, SORT_OPTIONS, CHECKOUT_STEPS, ORDER_ID_PREFIX,
  REGIONS, COUNTRIES, FAQ_ITEMS, REVIEWS,
} from "./Edata.js";
import { useEsim } from "./Edata";
import { addShopOrder } from "../../Users/Auth.js";
import { useAuth } from "../../../../context/AuthContext";
import esimService from "../../../../services/esimService";
import logoImg from "../../../../assets/PNG/RecieptLogo.png";
import { getWallet, spendRewardMoney } from "../../Users/ProfileLogic.js";
import Login from "../../Users/Login";
import "./Esimstyle.css";

// ─── Utility ────────────────────────────────────────────────
const useResponsiveConfig = (configKey) => {
  const [cfg, setCfg] = useState(UI_CONFIG[configKey].desktop);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w <= UI_CONFIG.breakpoints.mobile) setCfg(UI_CONFIG[configKey].mobile);
      else if (w <= UI_CONFIG.breakpoints.tablet) setCfg(UI_CONFIG[configKey].tablet);
      else setCfg(UI_CONFIG[configKey].desktop);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [configKey]);
  return cfg;
};

const searchCountriesFromApi = (countries, query, regionFilter = "all") => {
  const q = String(query || "").toLowerCase().trim();
  return (countries || []).filter((c) => {
    const regionMatch = regionFilter === "all" || c.region_id === regionFilter || c.region === regionFilter;
    if (!regionMatch) return false;
    if (!q) return true;
    const nameMatch = String(c.name || "").toLowerCase().includes(q);
    const aliasMatch = (c.aliases || []).some((a) => String(a).toLowerCase().includes(q));
    const codeMatch = String(c.flag || "").toLowerCase().includes(q);
    return nameMatch || aliasMatch || codeMatch;
  });
};

// ─── Terms Modal ─────────────────────────────────────────────
const TermsModal = ({ onClose }) => {
  const lines = TERMS_AND_CONDITIONS.trim().split("\n");
  return createPortal(
    <div className="E-modal-overlay" onClick={onClose}>
      <div className="E-modal E-terms-modal" onClick={e => e.stopPropagation()}>
        <div className="E-terms-header">
          <h2 className="E-modal-title">Terms &amp; Conditions</h2>
          <button className="E-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="E-terms-body">
          {lines.map((line, i) => {
            const trimmed = line.trim();
            if (!trimmed) return <div key={i} style={{ height: "0.5rem" }} />;
            if (trimmed.startsWith("**") && trimmed.endsWith("**") && trimmed.length > 4) {
              const heading = trimmed.slice(2, -2);
              return <h4 key={i} className="E-terms-heading">{heading}</h4>;
            }
            return <p key={i} className="E-terms-para">{trimmed}</p>;
          })}
        </div>
        <div className="E-terms-footer">
          <button className="E-btn-primary" onClick={onClose}>I Understand</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Floating Top-Right Actions ──────────────────────────────
const FloatingActions = ({ cart, navigate }) => {
  // return (
  //   <div className="E-floating-actions">
  //     <button className="E-fab-cart" onClick={() => navigate("cart")}>
  //       <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
  //         <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
  //         <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  //       </svg>
  //       <span className="E-fab-label">Cart</span>
  //       {cart.length > 0 && <span className="E-fab-badge">{cart.length}</span>}
  //     </button>
  //   </div>
  // );
};

// ─── Currency Selector ───────────────────────────────────────
const CurrencySelector = ({ currency, setCurrency }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const current = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  const toggleDropdown = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX });
    }
    setOpen(!open);
  };

  useEffect(() => {
    const close = () => setOpen(false);
    if (open) document.addEventListener("scroll", close, true);
    return () => document.removeEventListener("scroll", close, true);
  }, [open]);

  return (
    <div className="E-currency-wrap">
      <button className="E-currency-btn" ref={btnRef} onClick={toggleDropdown}>
        <span className="E-currency-symbol">{current.symbol}</span>
        <span className="E-currency-code">{current.code}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
      {open && createPortal(
        <div
          className="E-currency-dropdown"
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {CURRENCIES.map(c => (
            <button
              key={c.code}
              className={`E-currency-option ${c.code === currency ? "E-active" : ""}`}
              onClick={(e) => { e.stopPropagation(); setCurrency(c.code); setOpen(false); }}
            >
              <span>{c.symbol} {c.code}</span>
              <span className="E-currency-label">{c.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── Hero Search ─────────────────────────────────────────────
const HeroSearch = ({ navigate, countries = [], regions = [] }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const handleInput = (val) => {
    setQuery(val);
    if (val.length > 0) {
      const filtered = searchCountriesFromApi(countries, val).slice(0, 6);
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };

  const selectCountry = (country) => {
    setSuggestions([]);
    setQuery("");
    navigate("country", { countryId: country.id });
  };

  const handleSearch = () => {
    if (query.length > 0) {
      const results = searchCountriesFromApi(countries, query);
      if (results.length === 1) navigate("country", { countryId: results[0].id });
    }
  };

  return (
    <div className="E-hero-search-block">
      <div className="E-hero-search-box">
        <div className="E-search-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
        </div>
        <input
          className="E-hero-input"
          type="text"
          placeholder="Search destination country..."
          value={query}
          onChange={e => handleInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
        />
        <button className="E-search-go">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
        </button>
        {suggestions.length > 0 && (
          <div className="E-suggestions">
            {suggestions.map(c => (
              <button key={c.id} className="E-suggestion-item" onClick={() => selectCountry(c)}>
                <span className="E-sug-flag">{c.flag}</span>
                <span className="E-sug-name">{c.name}</span>
                <span className="E-sug-region">{regions.find(r => r.id === (c.region_id || c.region))?.name || ""}</span>
                <span className="E-sug-arrow"></span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="E-search-footer">
        {/* <CurrencySelector currency={currency} setCurrency={setCurrency} /> */}
        <span className="E-search-hint">Try: "Japan", "JP", "Dubai", "NYC"</span>
      </div>
    </div>
  );
};

// ─── Reviews Section with Search ────────────────────────────
const ReviewsSection = ({ reviews = [] }) => {
  const cfg = useResponsiveConfig("reviews");
  const [searchQ, setSearchQ] = useState("");
  const [visibleCount, setVisibleCount] = useState(cfg.visible);

  useEffect(() => { setVisibleCount(cfg.visible); }, [cfg.visible]);

  const filtered = reviews.filter(r => {
    const q = searchQ.toLowerCase();
    return !q || r.author.toLowerCase().includes(q) || r.destination.toLowerCase().includes(q) ||
      r.country.toLowerCase().includes(q) || r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q);
  });

  const visible = filtered.slice(0, visibleCount);

  return (
    <section className="E-section E-section-alt">
      <div className="E-container">
        <div className="E-section-header" data-aos="fade-up">
          <h2 className="E-section-title">What Travellers Say</h2>
          <p className="E-section-sub">Real experiences from real travellers around the world</p>
        </div>
        <div className="E-reviews-search-bar" data-aos="fade-up">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search reviews by destination, traveller, or keyword..."
            value={searchQ}
            onChange={e => { setSearchQ(e.target.value); setVisibleCount(UI_CONFIG.reviews.desktop.visible + UI_CONFIG.reviews.loadMore); }}
          />
          {searchQ && (
            <button className="E-reviews-clear" onClick={() => setSearchQ("")}>✕</button>
          )}
        </div>
        {filtered.length === 0 ? (
          <div className="E-no-results">No reviews match your search.</div>
        ) : (
          <>
            <div className="E-reviews-grid">
              {visible.map((r, i) => (
                <div key={r.id} className="E-review-card" data-aos="fade-up" data-aos-delay={i * 60}>
                  <div className="E-review-rating">
                    {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                  </div>
                  <h4 className="E-review-title">{r.title}</h4>
                  <p className="E-review-body">{r.body}</p>
                  <div className="E-review-author">
                    <div className="E-review-avatar">{r.author[0]}</div>
                    <div>
                      <span className="E-review-name">{r.author}</span>
                      <span className="E-review-meta">{r.country}  {r.destination} · {r.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="E-view-more-wrap">
              {visibleCount < filtered.length && (
                <button className="E-view-more-btn" onClick={() => setVisibleCount(v => v + UI_CONFIG.reviews.loadMore)}>
                  View More Reviews
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
              )}
              {visibleCount > cfg.visible && (
                <button className="E-view-more-btn" onClick={() => setVisibleCount(cfg.visible)}>
                  View Less
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ─── Countries Grid with Search + Filter ─────────────────────
const CountriesGrid = ({ navigate, currency, countries = [], regions = [] }) => {
  const cfg = useResponsiveConfig("browseCountries");
  const [searchQ, setSearchQ] = useState("");
  const [activeRegion, setActiveRegion] = useState("all");
  const [visibleCount, setVisibleCount] = useState(cfg.visible);
  const [sortBy, setSortBy] = useState("popular");

  useEffect(() => { setVisibleCount(cfg.visible); }, [cfg.visible]);

  const filtered = searchCountriesFromApi(countries, searchQ, activeRegion);

  const sorted = [...filtered].filter(country => {
    if (sortBy === 'popular') return country.popular;
    if (sortBy === 'mostvisited') return country.mostVisited;
    if (sortBy === 'toppicks') return country.topPicks;
    if (sortBy === 'trending') return country.trending;
    if (sortBy === 'bestdeal') return country.bestDeal;
    if (sortBy === 'mostloved') return country.mostLoved;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'popular') return (b.popular ? 1 : 0) - (a.popular ? 1 : 0);
    if (sortBy === 'mostvisited') return (b.mostVisited ? 1 : 0) - (a.mostVisited ? 1 : 0);
    if (sortBy === 'toppicks') return (b.topPicks ? 1 : 0) - (a.topPicks ? 1 : 0);
    if (sortBy === 'trending') return (b.trending ? 1 : 0) - (a.trending ? 1 : 0);
    if (sortBy === 'bestdeal') return (b.bestDeal ? 1 : 0) - (a.bestDeal ? 1 : 0);
    if (sortBy === 'mostloved') return (b.mostLoved ? 1 : 0) - (a.mostLoved ? 1 : 0);
    return 0;
  });

  const visible = sorted.slice(0, visibleCount);

  const handleRegion = (id) => {
    setActiveRegion(id);
    setVisibleCount(cfg.visible);
  };

  return (
    <section className="E-section">
      <div className="E-container">
        <div className="E-section-header" data-aos="fade-up">
          <h2 className="E-section-title">Browse All Countries</h2>
          <p className="E-section-sub">Find your perfect eSIM plan from our global network of 100+ destinations</p>
        </div>

        <div className="E-countries-controls" data-aos="fade-up">
          <div className="E-controls-row">
            <div className="E-countries-search-bar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Filter by country name, city, or code..."
                value={searchQ}
                onChange={e => { setSearchQ(e.target.value); setVisibleCount(cfg.visible); }}
              />
              {searchQ && (
                <button className="E-countries-clear" onClick={() => setSearchQ("")}>✕</button>
              )}
            </div>

            <div className="E-sort-dropdown">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="E-filter-chips">
            <button
              className={`E-chip ${activeRegion === "all" ? "E-active" : ""}`}
              onClick={() => handleRegion("all")}
            >All</button>
            {regions.map(r => (
              <button
                key={r.id}
                className={`E-chip ${activeRegion === r.id ? "E-active" : ""}`}
                onClick={() => handleRegion(r.id)}
              >{r.name}</button>
            ))}
          </div>
        </div>

        <div className="E-countries-meta" data-aos="fade-up">
          <span>{sorted.length} destination{sorted.length !== 1 ? "s" : ""} found</span>
        </div>

        {sorted.length === 0 ? (
          <div className="E-no-results">No countries match your search. Try a different keyword.</div>
        ) : (
          <>
            <div className="E-countries-marquee-outer">
              <div className="E-countries-marquee-track">
                {[...sorted, ...sorted].map((country, i) => (
                  <button
                    key={`${country.id}-${i}`}
                    className="E-country-card"
                    onClick={() => navigate("country", { countryId: country.id })}
                  >
                    <div className="E-country-img-wrap">
                      <img src={country.heroImage} alt={country.name} loading="lazy" />
                      <div className="E-country-overlay"></div>
                    </div>
                    <div className="E-country-body">
                      {sortBy === 'popular' && country.popular && <div className="E-country-popular-tag">Popular</div>}
                      {sortBy === 'bestdeal' && country.bestDeal && <div className="E-country-best-deal-tag">Best Deal</div>}
                      {sortBy === 'toppicks' && country.topPicks && <div className="E-country-top-pick-tag">Top Pick</div>}
                      {sortBy === 'mostloved' && country.mostLoved && <div className="E-country-most-loved-tag">Most Loved</div>}
                      {sortBy === 'mostvisited' && country.mostVisited && <div className="E-country-most-visited-tag">Most Visited</div>}
                      {sortBy === 'trending' && country.trending && <div className="E-country-trending-tag">Trending</div>}
                      <h3 className="E-country-name">{country.name}</h3>
                      <div className="E-country-price-row">
                        <span className="E-country-from">From</span>
                        <span className="E-country-price">{convertPrice(4.99, currency)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ─── FAQ Section ─────────────────────────────────────────────
const FAQSection = ({ sectionAlt = false, faqItems = [] }) => {
  const [open, setOpen] = useState(null);
  const cfg = useResponsiveConfig("faq");
  const [visibleCount, setVisibleCount] = useState(cfg.visible);

  useEffect(() => { setVisibleCount(cfg.visible); }, [cfg.visible]);

  const visibleItems = faqItems.slice(0, visibleCount);
  const hasMore = visibleCount < faqItems.length;
  const hasLess = visibleCount > cfg.visible;

  return (
    <section className={`E-section ${sectionAlt ? "E-section-alt" : ""}`}>
      <div className="E-container">
        <div className="E-section-header" data-aos="fade-up">
          <h2 className="E-section-title">Frequently Asked Questions</h2>
          <p className="E-section-sub">Everything you need to know about theVisaCo eSIMs</p>
        </div>
        <div className="E-faq-list">
          {visibleItems.map((item) => (
            <div key={item.id} className={`E-faq-item ${open === item.id ? "E-open" : ""}`}>
              <button className="E-faq-question" onClick={() => setOpen(open === item.id ? null : item.id)}>
                <span>{item.question}</span>
                <span className="E-faq-toggle">{open === item.id ? "−" : "+"}</span>
              </button>
              {open === item.id && (
                <div className="E-faq-answer">{item.answer}</div>
              )}
            </div>
          ))}
        </div>
        <div className="E-view-more-wrap">
          {hasMore && (
            <button className="E-view-more-btn" onClick={() => setVisibleCount(v => v + UI_CONFIG.faq.loadMore)}>
              View More FAQ
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </button>
          )}
          {hasLess && (
            <button className="E-view-more-btn" onClick={() => setVisibleCount(cfg.visible)}>
              View Less
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Home Page ───────────────────────────────────────────────
const HomePage = ({ navigate, currency, countries = [], regions = [], faqItems = [], reviews = [] }) => {
  const cfg = useResponsiveConfig("popularDestinations");
  const popularCountries = countries.filter(c => c.is_popular || c.popular);
  const [visiblePopular, setVisiblePopular] = useState(cfg.visible);

  useEffect(() => { setVisiblePopular(cfg.visible); }, [cfg.visible]);

  const visiblePop = popularCountries.slice(0, visiblePopular);

  return (
    <main className="E-home-page">
      {/* Hero */}
      <section className="E-hero">
        <div className="E-hero-bg">
          <div className="E-hero-orb E-hero-orb-1"></div>
          <div className="E-hero-orb E-hero-orb-2"></div>
          <div className="E-hero-orb E-hero-orb-3"></div>
          <div className="E-hero-grid"></div>
        </div>
        <div className="E-hero-content">
          <h1 className="E-hero-title" data-aos="fade-up">
            Travel Without<br />
            <em>Boundaries</em>
          </h1>
          <div data-aos="fade-up" data-aos-delay="200">
            <HeroSearch navigate={navigate} countries={countries} regions={regions} />
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="E-section">
        <div className="E-container">
          <div className="E-section-header" data-aos="fade-up">
            <h2 className="E-section-title">Popular Destinations</h2>
            <p className="E-section-sub">Handpicked plans for the world's most visited countries</p>
          </div>
          <div className="E-popular-grid">
            {visiblePop.map((country, i) => (
              <button
                key={country.id}
                className="E-popular-card"
                onClick={() => navigate("country", { countryId: country.id })}
                data-aos="fade-up"
                data-aos-delay={i * 60}
              >
                <div className="E-popular-img">
                  <img src={country.heroImage} alt={country.name} loading="lazy" />
                  <div className="E-popular-overlay"></div>
                </div>
                <div className="E-popular-body">
                  <h3 className="E-popular-name">{country.name}</h3>
                  <span className="E-popular-region">{regions.find(r => r.id === (country.region_id || country.region))?.name || ""}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="E-view-more-wrap" style={{ marginTop: "2rem" }}>
            {visiblePopular < popularCountries.length && (
              <button className="E-view-more-btn" onClick={() => setVisiblePopular(v => v + UI_CONFIG.popularDestinations.loadMore)}>
                View More
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </button>
            )}
            {visiblePopular > cfg.visible && (
              <button className="E-view-more-btn" onClick={() => setVisiblePopular(cfg.visible)}>
                View Less
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Countries with Search */}
      <CountriesGrid navigate={navigate} currency={currency} countries={countries} regions={regions} />

      {/* Features */}
      <section className="E-section E-features-section">
        <div className="E-container">
          <div className="E-section-header" data-aos="fade-up">
            <h2 className="E-section-title">Built for Modern Travellers</h2>
          </div>
          <div className="E-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="E-feature-card" data-aos="fade-up" data-aos-delay={i * 80}>
                <h3 className="E-feature-title">{f.title}</h3>
                <p className="E-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews with Search */}
      <ReviewsSection reviews={reviews} />

      {/* FAQ on Home */}
      <FAQSection faqItems={faqItems} />

      {/* CTA */}
      <section className="E-cta-section" data-aos="fade-up">
        <div className="E-container">
          <div className="E-cta-box">
            <div className="E-cta-glow"></div>
            <h2 className="E-cta-title">Ready to Travel Connected?</h2>
            <p className="E-cta-sub">Join over 500,000 travellers who use theVisaCo for seamless global connectivity.</p>
            <button className="E-cta-btn" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              Find Your Plan
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

// ─── Country Page ────────────────────────────────────────────
const CountryPage = ({ countryId, navigate, addToCart, currency, countries = [], planTemplates = [], faqItems = [] }) => {
  const country = countries.find(c => c.id === countryId);
  const [plans, setPlans] = useState([]);
  const [maxDays, setMaxDays] = useState(365);
  const [activeType, setActiveType] = useState("all");
  const [added, setAdded] = useState({});

  useEffect(() => {
    if (!country) return;
    const normalized = (planTemplates || []).map((p) => ({
      id: p.id,
      type: p.type || "data",
      label: p.label || p.name || "Plan",
      data: p.data_allowance || p.data || "",
      validity: Number(p.validity_days || p.validity || 0),
      price: Number(p.price_usd || p.price || 0),
      features: Array.isArray(p.features) ? p.features : [],
      popular: !!p.popular,
      hasVoice: false,
      hasSMS: false,
    }));
    setPlans(normalized);
  }, [country, planTemplates]);

  if (!country) return <div className="E-not-found">Country not found.</div>;

  const filteredPlans = plans.filter(p => {
    const typeMatch = activeType === "all" || p.type === activeType;
    return typeMatch && p.validity <= maxDays;
  });

  const handleAddToCart = (plan) => {
    addToCart({ ...plan, countryId: country.id, countryName: country.name });
    setAdded(prev => ({ ...prev, [plan.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [plan.id]: false })), 1500);
  };

  return (
    <main className="E-country-page">
      <div className="E-country-hero">
        <button className="E-back-btn" onClick={() => navigate("home")}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          Back
        </button>
        <div className="E-country-hero-img-wrap">
          <img src={country.heroImage} alt={country.name} className="E-country-hero-img" />
          <div className="E-country-hero-overlay"></div>
          <div className="E-country-hero-curve"></div>
        </div>
        <div className="E-country-hero-content">
          <h1 className="E-country-hero-title">{country.name}</h1>
          <p className="E-country-hero-desc">{country.description}</p>
          <div className="E-country-networks">
            <span className="E-networks-label">Partner Networks</span>
            {country.networks.map(n => <span key={n} className="E-network-tag">{n}</span>)}
          </div>
        </div>
      </div>

      <div className="E-plans-section">
        <div className="E-container">
          <div className="E-section-header">
            <h2 className="E-section-title">Available Plans</h2>
            <p className="E-section-sub">Choose the best eSIM plan for your trip</p>
          </div>
          <div className="E-plans-filters" data-aos="fade-up">
            <div className="E-filter-group">
              <label className="E-filter-label">Plan Type</label>
              <div className="E-filter-chips">
                {PLAN_TYPE_FILTERS.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`E-chip ${activeType === value ? "E-active" : ""}`}
                    onClick={() => setActiveType(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="E-filter-group">
              <label className="E-filter-label">
                Max Validity: <strong>{maxDays} days</strong>
              </label>
              <div className="E-slider-wrap">
                <input
                  type="range"
                  min={1}
                  max={365}
                  value={maxDays}
                  onChange={e => setMaxDays(Number(e.target.value))}
                  className="E-days-slider"
                  style={{ "--E-slider-pct": `${((maxDays - 1) / 364) * 100}%` }}
                />
                <div className="E-slider-labels">
                  <span>1 day</span>
                  <span>365 days</span>
                </div>
              </div>
            </div>
          </div>

          <div className="E-plans-count" data-aos="fade-up">
            {filteredPlans.length} plan{filteredPlans.length !== 1 ? "s" : ""} available
          </div>

          <div className="E-plans-grid">
            {filteredPlans.map((plan, i) => (
              <div
                key={plan.id}
                className={`E-plan-card ${plan.popular ? "E-plan-popular" : ""}`}
                data-aos="fade-up"
                data-aos-delay={i * 50}
              >
                {plan.popular && <div className="E-plan-popular-badge">Most Popular</div>}
                <div className="E-plan-header">
                  <div className="E-plan-type-tag">{getPlanTypeLabel(plan.type)}</div>
                  <h3 className="E-plan-label">{plan.label}</h3>
                  <div className="E-plan-data">{plan.data}</div>
                  {plan.hasVoice && <div className="E-plan-voice">{plan.voice} Voice</div>}
                  {plan.hasSMS && <div className="E-plan-sms">{plan.sms}</div>}
                </div>
                <div className="E-plan-validity">
                  Valid for {plan.validity} day{plan.validity !== 1 ? "s" : ""}
                </div>
                <ul className="E-plan-features">
                  {plan.features.map(f => (
                    <li key={f} className="E-plan-feature">{f}</li>
                  ))}
                </ul>
                <div className="E-plan-footer">
                  <div className="E-plan-price">{convertPrice(plan.price, currency)}</div>
                  <button
                    className={`E-add-to-cart-btn ${added[plan.id] ? "E-added" : ""}`}
                    onClick={() => handleAddToCart(plan)}
                  >
                    {added[plan.id] ? "✓ Added" : "Add to Cart"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredPlans.length === 0 && (
            <div className="E-no-plans">
              No plans match your filters. Try adjusting the validity slider or plan type.
            </div>
          )}
        </div>
      </div>

          <FAQSection sectionAlt={true} faqItems={faqItems} />
    </main>
  );
};

// ─── Cart Page ───────────────────────────────────────────────
const CartPage = ({ cart, setCart, navigate, currency }) => {
  const removeItem = (id) => setCart(c => c.filter(item => item.id !== id));

  if (cart.length === 0) return (
    <div className="E-empty-state-page">
      <div className="E-empty-state">
        <div className="E-empty-icon">🛒</div>
        <h2>Your cart is empty</h2>
        <p>Browse our destinations and add an eSIM plan to get started.</p>
        <button className="E-btn-primary" onClick={() => navigate("home")}>Browse Plans</button>
      </div>
    </div>
  );

  return (
    <main className="E-inner-page">
      <div className="E-container">
        <div className="E-page-header" data-aos="fade-up">
          <h1 className="E-page-title">Your Cart</h1>
          <p className="E-page-sub">{cart.length} item{cart.length !== 1 ? "s" : ""} — each processed as a separate transaction</p>
        </div>
        <div className="E-cart-items-standalone">
          {cart.map(item => (
            <div key={item.id} className="E-cart-item-standalone" data-aos="fade-up">
              <div className="E-cart-item-body">
                <div className="E-cart-item-flag">{item.countryName?.slice(0, 2).toUpperCase()}</div>
                <div className="E-cart-item-info">
                  <h4 className="E-cart-item-name">{item.countryName} — {item.label}</h4>
                  <span className="E-cart-item-type">{getPlanTypeLabel(item.type)}</span>
                  <span className="E-cart-item-validity">{item.validity} days · {item.data}</span>
                </div>
              </div>
              <div className="E-cart-item-actions">
                <span className="E-cart-item-price">{convertPrice(item.price, currency)}</span>
                <button
                  className="E-btn-primary E-btn-sm"
                  onClick={() => navigate("checkout", { item })}
                >
                  Pay Now
                </button>
                <button className="E-remove-btn" onClick={() => removeItem(item.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <button className="E-btn-ghost" onClick={() => navigate("home")}>Continue Shopping</button>
        </div>
      </div>
    </main>
  );
};

// ─── Single-Item Checkout Page ───────────────────────────────
const CheckoutPage = ({ item, setCart, navigate, currency }) => {
  const [step, setStep] = useState(1);
  const [payMethod, setPayMethod] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [bank, setBank] = useState("");
  const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "" });
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [simType, setSimType] = useState("esim");
  const [isEsimCompatible, setIsEsimCompatible] = useState(false);
  const [error, setError] = useState("");
  const [useRewardDiscount, setUseRewardDiscount] = useState(false);
  const [rewardDiscountInput, setRewardDiscountInput] = useState("");
  const [discountError, setDiscountError] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    whatsapp: "",
    whatsappCode: "+91",
    address: "",
    city: "",
    pincode: "",
    termsAccepted: false,
  });

  const generateOrderId = () => ORDER_ID_PREFIX + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 4).toUpperCase();
  const [orderId] = useState(() => generateOrderId());

  const { esimState } = useEsim();
  const { cart, user: esimUser } = esimState;
  const { user: authUser } = useAuth();
  const user = esimUser || authUser;
  const userEmail = user?.email;
  const wallet = userEmail ? getWallet(userEmail) : null;
  const redeemableBalance = wallet?.redeemable?.balance || 0;
  const nonRedeemableBalance = wallet?.nonRedeemable?.balance || 0;
  const walletBalance = redeemableBalance + nonRedeemableBalance;
  const isRewardLocked = !userEmail;

  useEffect(() => {
    if (payMethod === "wallet") {
      setUseRewardDiscount(false);
      setRewardDiscountInput("");
      setDiscountError("");
    }
  }, [payMethod]);

  if (!item) return (
    <div className="E-empty-state-page">
      <div className="E-empty-state">
        <div className="E-empty-icon">🛒</div>
        <h2>No item selected</h2>
        <p>Please select a plan from your cart to checkout.</p>
        <button className="E-btn-primary" onClick={() => navigate("cart")}>Back to Cart</button>
      </div>
    </div>
  );

  const convFeeUSD = PAYMENT_CONVENIENCE_FEES[payMethod] || 0;
  const subtotalUSD = item.price * quantity;
  const taxUSD = subtotalUSD * TAX_RATE;
  const chargesOnly = subtotalUSD;
  const maxDiscountINR = chargesOnly * MAX_REWARD_DISCOUNT_PCT * 83;
  const totalBeforeDiscount = subtotalUSD + taxUSD + convFeeUSD;
  const totalPriceINR = totalBeforeDiscount * 83;

  let rewardDiscount = 0;
  let appliedDiscount = 0;

  if (payMethod === "wallet") {
    appliedDiscount = Math.min(walletBalance / 83, totalBeforeDiscount);
  } else if (useRewardDiscount && userEmail && walletBalance > 0) {
    const maxAllowedDiscountUSD = chargesOnly * MAX_REWARD_DISCOUNT_PCT;
    const inputAmountINR = rewardDiscountInput ? parseFloat(rewardDiscountInput) : 0;
    const inputAmountUSD = inputAmountINR / 83;
    const cappedInputUSD = Math.min(inputAmountUSD, maxAllowedDiscountUSD);
    rewardDiscount = Math.min(cappedInputUSD, walletBalance / 83);
    appliedDiscount = rewardDiscount;
  }

  const totalUSD = totalBeforeDiscount - appliedDiscount;
  const finalPayable = totalUSD;

  const handleDownloadReceipt = () => {
    const html = generateReceiptHTML({ item, quantity, payMethod, billingDetails, orderId, currency, appliedDiscount, logoSrc: logoImg, upiId, bank, card, simType });
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TheVisaCo-Receipt-${orderId}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDFReceipt = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;
      const toBase64 = async (url) => {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return new Promise((r) => {
          const reader = new FileReader();
          reader.onloadend = () => r(reader.result);
          reader.readAsDataURL(blob);
        });
      };
      let logoSrc = null;
      try { logoSrc = await toBase64(logoImg); } catch { logoSrc = null; }
      const html = generateReceiptHTML({ item, quantity, payMethod, billingDetails, orderId, currency, appliedDiscount, logoSrc, pdf: true, upiId, bank, card, simType });
      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#f5f9ff";
      document.body.appendChild(container);
      const el = container.querySelector(".Receipt-card") || container.firstElementChild;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
      document.body.removeChild(container);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`TheVisaCo-Receipt-${orderId}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      handleDownloadReceipt();
    }
  };

  const handlePlaceOrder = async () => {
    if (payMethod === "wallet" && userEmail) {
      const amountINR = customRound(appliedDiscount * 83);
      if (amountINR > 0) {
        const result = spendRewardMoney(userEmail, amountINR, `eSIM Purchase — ${item.countryName} ${item.label}`);
        if (!result.success) {
          setError("Insufficient reward balance. Please choose another payment method.");
          return;
        }
      }
    } else if (useRewardDiscount && userEmail && rewardDiscount > 0) {
      const amountINR = customRound(rewardDiscount * 83);
      const result = spendRewardMoney(userEmail, amountINR, `eSIM Discount — ${item.countryName} ${item.label}`);
      if (!result.success) {
        setError("Insufficient reward balance. Please choose another payment method.");
        return;
      }
    }

    if (userEmail) {
      const rewardUsedINR = payMethod === "wallet" ? customRound(appliedDiscount * 83) : (useRewardDiscount && rewardDiscount > 0 ? customRound(rewardDiscount * 83) : 0);
      const totalPaid = totalBeforeDiscount * 83;
      const actualPaid = totalPaid - rewardUsedINR;
      
      let paymentMethod;
      if (rewardUsedINR > 0) {
        if (actualPaid === 0) {
          paymentMethod = "Reward";
        } else {
          const baseMethod = payMethod === "wallet" ? "Wallet" : payMethod === "card" ? "Card" : payMethod === "upi" ? "UPI" : "Bank Transfer";
          paymentMethod = `Reward + ${baseMethod}`;
        }
      } else {
        paymentMethod = payMethod === "wallet" ? "Wallet" : payMethod === "card" ? "Card" : payMethod === "upi" ? "UPI" : "Bank Transfer";
      }
      
      const orderData = {
        id: orderId,
        order_id: orderId,
        date: new Date().toISOString(),
        status: "Confirmed",
        paymentMethod: paymentMethod,
        total: totalPaid,
        amount: totalPaid,
        productName: `eSIM - ${item.label}`,
        country: item.countryName,
        planName: item.planName,
        quantity: quantity,
        email: userEmail,
        rewardUsed: rewardUsedINR,
        actualPaid: actualPaid,
        totalPrice: totalPaid,
      };
      addShopOrder(userEmail, orderData);
    }

    setOrderPlaced(true);
    setCart(prev => prev.filter(i => i.id !== item.id));
  };

  const getPaymentError = () => {
    if (!payMethod) return "Please select a payment method";
    if (payMethod === "upi" && !upiId.trim()) return "Please enter your UPI ID";
    if (payMethod === "netbanking" && !bank.trim()) return "Please select a bank";
    if (payMethod === "card" && !card.number.trim()) return "Please enter card number";
    if (payMethod === "card" && !card.name.trim()) return "Please enter name on card";
    if (payMethod === "card" && !card.expiry.trim()) return "Please enter expiry date";
    if (payMethod === "card" && !card.cvv.trim()) return "Please enter CVV";
    if (payMethod === "wallet" && walletBalance < totalBeforeDiscount * 83) return `Insufficient reward balance. You have ₹${customRound(walletBalance)} but need ₹${customRound(totalBeforeDiscount * 83)}`;
    if (useRewardDiscount && !rewardDiscountInput) return "Please enter discount amount";
    if (useRewardDiscount && rewardDiscountInput && parseInt(rewardDiscountInput, 10) > customRound(maxDiscountINR)) return `Discount cannot exceed ₹${customRound(maxDiscountINR)}`;
    if (useRewardDiscount && parseFloat(rewardDiscountInput) > walletBalance) return `Insufficient balance. You have ₹${customRound(walletBalance)}`;
    return "";
  };

  const canReview =
    billingDetails.firstName && billingDetails.lastName &&
    billingDetails.email &&
    billingDetails.country && billingDetails.whatsapp &&
    billingDetails.termsAccepted &&
    payMethod &&
    (payMethod === "upi" ? upiId.trim() : true) &&
    (payMethod === "netbanking" ? bank.trim() : true) &&
    (payMethod === "card" ? (card.number.trim() && card.name.trim() && card.expiry.trim() && card.cvv.trim()) : true) &&
    (payMethod === "wallet" ? (userEmail ? true : false) : true) &&
    (useRewardDiscount ? (userEmail ? true : false) : true) &&
    (simType === "esim" || (billingDetails.address && billingDetails.city && billingDetails.pincode));

  if (orderPlaced) return (
    <div className="E-empty-state-page">
      <div className="E-order-success">
        <div className="E-success-icon">✓</div>
        <h2>Payment Confirmed!</h2>
        <p>Order ID: <strong>{orderId}</strong></p>
        <p>Your eSIM QR code will be delivered to <strong>{billingDetails.email || "your email"}</strong> within minutes.</p>
        <div className="E-success-actions">
          <button className="E-btn-primary" onClick={handleDownloadPDFReceipt}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download PDF Receipt
          </button>
          <button className="E-btn-ghost" onClick={() => navigate("home")}>Continue Shopping</button>
        </div>
      </div>
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    </div>
  );

  return (
    <main className="E-inner-page">
      <div className="E-container">
        <div className="E-page-header" data-aos="fade-up">
          <h1 className="E-page-title">Checkout</h1>
          <p className="E-page-sub">{item.countryName} — {item.label}</p>
        </div>

        <div className="E-checkout-steps">
          {CHECKOUT_STEPS.map((s, i) => (
            <div key={s} className={`E-checkout-step ${step >= i + 1 ? "E-active" : ""}`}>
              <span className="E-step-num">{i + 1}</span>
              <span className="E-step-label">{s}</span>
            </div>
          ))}
        </div>

        <div className="E-checkout-layout">
          <div className="E-checkout-main">

            {/* Step 1: SIM type + billing */}
            {step === 1 && (
              <div className="E-checkout-block" data-aos="fade-up">
                <h3 className="E-checkout-block-title">Select SIM Type</h3>
                <div className="E-sim-type-selector">
                  <button
                    className={`E-sim-type-btn ${simType === "esim" ? "E-selected" : ""}`}
                    onClick={() => setSimType("esim")}
                  >
                    {/* <div className="E-sim-type-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12" y2="18"/>
                      </svg>
                    </div> */}
                    <div className="E-sim-type-info">
                      <span className="E-sim-type-label">eSIM</span>
                      <span className="E-sim-type-desc">Digital — Instant email delivery</span>
                    </div>
                  </button>
                  <button
                    className={`E-sim-type-btn ${simType === "physical" ? "E-selected" : ""}`}
                    onClick={() => setSimType("physical")}
                  >
                    {/* <div className="E-sim-type-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="4" y="4" width="16" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="2" x2="12" y2="22"/>
                      </svg>
                    </div> */}
                    <div className="E-sim-type-info">
                      <span className="E-sim-type-label">Physical SIM</span>
                      <span className="E-sim-type-desc">Card ships to your address</span>
                    </div>
                  </button>
                </div>

                <div className="E-checkout-product-display">
                  <div className="E-product-flag">{item.countryName?.slice(0, 2).toUpperCase()}</div>
                  <div className="E-product-info">
                    <span className="E-product-name">{item.countryName}</span>
                    <span className="E-product-details">{item.validity} Days • {item.data}</span>
                  </div>
                </div>

                <div className="E-quantity-selector">
                  <label className="E-quantity-label">Quantity:</label>
                  <div className="E-quantity-controls">
                    <button className="E-qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1}>−</button>
                    <span className="E-qty-value">{quantity}</span>
                    <button className="E-qty-btn" onClick={() => setQuantity(q => q + 1)}>+</button>
                  </div>
                </div>

                {simType === "esim" && (
                  <div className="E-esim-compatibility">
                    <label className="E-compatibility-checkbox">
                      <input
                        type="checkbox"
                        checked={isEsimCompatible}
                        onChange={e => setIsEsimCompatible(e.target.checked)}
                      />
                      <span className="E-checkbox-mark"></span>
                      <span className="E-checkbox-label">My phone is eSIM compatible</span>
                    </label>
                    <p className="E-compatibility-note">Please ensure your device supports eSIM before ordering.</p>
                  </div>
                )}

                <button className="E-btn-primary E-btn-full" onClick={() => setStep(2)} style={{ marginTop: "0.5rem" }}>
                  Continue to Billing
                </button>
              </div>
            )}

            {/* Step 2: Billing + payment */}
            {step === 2 && (
              <div className="E-checkout-block" data-aos="fade-up">
                <h3 className="E-checkout-block-title">Billing Details</h3>
                <div className="E-billing-form">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    <div className="E-form-field">
                      <label>First Name</label>
                      <input placeholder="First Name" value={billingDetails.firstName} onChange={e => setBillingDetails(p => ({ ...p, firstName: e.target.value }))} />
                    </div>
                    <div className="E-form-field">
                      <label>Last Name</label>
                      <input placeholder="Last Name" value={billingDetails.lastName} onChange={e => setBillingDetails(p => ({ ...p, lastName: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    <div className="E-form-field">
                      <label>Email Address</label>
                      <input type="email" placeholder="Email Address" value={billingDetails.email} onChange={e => setBillingDetails(p => ({ ...p, email: e.target.value }))} />
                    </div>
                    <div className="E-form-field">
                      <label>Country</label>
                      <input type="text" placeholder="Enter your country" value={billingDetails.country} onChange={e => setBillingDetails(p => ({ ...p, country: e.target.value }))} />
                    </div>
                  </div>
                  {simType === "physical" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                      <div className="E-form-field">
                        <label>City <span className="E-required">*</span></label>
                        <input placeholder="City" value={billingDetails.city} onChange={e => setBillingDetails(p => ({ ...p, city: e.target.value }))} />
                      </div>
                      <div className="E-form-field">
                        <label>Pincode <span className="E-required">*</span></label>
                        <input placeholder="Pincode" value={billingDetails.pincode} onChange={e => setBillingDetails(p => ({ ...p, pincode: e.target.value }))} maxLength={10} />
                      </div>
                    </div>
                  )}

                  {simType === "physical" && (
                    <div className="E-shipping-notice">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
                      </svg>
                      <span>Physical SIM cards will be shipped to your address. Delivery takes 7-10 business days.</span>
                    </div>
                  )}

                  <div style={simType === "physical" ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" } : {}}>
                    {simType === "physical" && (
                      <div className="E-form-field">
                        <label>Full Address <span className="E-required">*</span></label>
                        <textarea placeholder="House/Flat No., Building, Street, Area" value={billingDetails.address} onChange={e => setBillingDetails(p => ({ ...p, address: e.target.value }))} rows={3} />
                      </div>
                    )}

                    <div className="E-form-field">
                      <label>WhatsApp Number</label>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <select
                          value={billingDetails.whatsappCode}
                          onChange={e => setBillingDetails(p => ({ ...p, whatsappCode: e.target.value }))}
                          style={{ width: "120px", flexShrink: 0, appearance: "auto", WebkitAppearance: "auto", paddingRight: "1.5rem" }}
                        >
                          {WHATSAPP_COUNTRY_CODES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                        <input type="tel" placeholder="Phone number" value={billingDetails.whatsapp} onChange={e => setBillingDetails(p => ({ ...p, whatsapp: e.target.value }))} style={{ flex: 1 }} />
                      </div>
                    </div>
                  </div>

                  <label className="E-checkbox-field">
                    <input type="checkbox" checked={billingDetails.termsAccepted} onChange={e => setBillingDetails(p => ({ ...p, termsAccepted: e.target.checked }))} />
                    <span className="E-checkbox-mark"></span>
                    <span>
                      I agree to the{" "}
                      <button
                        className="E-terms-link"
                        onClick={() => setShowTerms(true)}
                        type="button"
                      >
                        Terms &amp; Conditions
                      </button>
                      {" "}and Privacy Policy
                    </span>
                  </label>
                </div>

                <h3 className="E-checkout-block-title" style={{ marginTop: "0.5rem" }}>Payment Method</h3>
                <div className="E-payment-methods">
                  {PAYMENT_METHODS.map(pm => {
                    const isWallet = pm.id === "wallet";
                    const isLocked = isWallet && (!userEmail || walletBalance < totalPriceINR);
                    return (
                      <div
                        key={pm.id}
                        className={`E-payment-method-card ${payMethod === pm.id ? "E-selected" : ""} ${isLocked ? "E-disabled" : ""}`}
                        onClick={() => !isLocked && setPayMethod(pm.id)}
                        style={isLocked ? { cursor: "not-allowed", position: "relative" } : {}}
                      >
                        <div className="E-pm-radio"></div>
                        <div className="E-pm-body">
                          <strong>{pm.label}</strong>
                          <span>{pm.description}</span>
                        </div>
                        <div className="E-pm-fee">
                          {PAYMENT_CONVENIENCE_FEES[pm.id] === 0
                            ? <span className="E-fee-free">Free</span>
                            : <span className="E-fee-amount">+{convertPrice(PAYMENT_CONVENIENCE_FEES[pm.id], "INR")}</span>
                          }
                        </div>
                      {isLocked && isWallet && (
                        <div style={{ position: "absolute", right: 12, top: 12, color: "var(--E-text-secondary)", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: 4 }}>
                          {!userEmail ? (
                            <span
                              style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                              onClick={() => setShowLoginModal(true)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              Sign in 
                            </span>
                          ) : (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                              Need ₹{customRound(totalPriceINR)} balance
                            </>
                          )}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>

                {/* Reward discount toggle — show for all, locked if not signed in */}
                {payMethod !== "wallet" && (userEmail ? walletBalance > 0 : true) && (
                  <div className={`E-reward-discount-option ${isRewardLocked ? 'E-locked' : ''}`} style={{ marginTop: "var(--E-space-md)", position: "relative" }}>
                    <label
                      className="E-reward-discount-checkbox"
                      onClick={() => {
                        if (isRewardLocked) return;
                        setUseRewardDiscount(!useRewardDiscount);
                        if (!useRewardDiscount) {
                          setRewardDiscountInput("");
                          setDiscountError("");
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--E-space-sm)",
                        cursor: isRewardLocked ? "not-allowed" : "pointer",
                        padding: "var(--E-space-sm)",
                        border: "1.5px solid var(--E-border)",
                        borderRadius: "var(--E-radius-md)",
                        background: isRewardLocked ? "var(--E-off-white)" : useRewardDiscount ? "var(--E-blue-pale)" : "var(--E-off-white)",
                        opacity: isRewardLocked ? 0.6 : 1,
                      }}
                    >
                      <div style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: useRewardDiscount ? "var(--E-blue)" : "var(--E-border)",
                        background: useRewardDiscount ? "var(--E-blue)" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {useRewardDiscount && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--E-navy)" }}>Use Reward Money for Discount</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--E-text-secondary)" }}>
                          {isRewardLocked ? (
                            <span style={{ color: "var(--E-danger)", display: "flex", alignItems: "center", gap: 4 }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              Sign in to unlock
                            </span>
                          ) : (
                            `Max discount: ₹${customRound(maxDiscountINR)} (20% of charges)`
                          )}
                        </div>
                      </div>
                      {isRewardLocked && (
                        <div style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          // background: "var(--E-navy)",
                          color: "black",
                          padding: "4px 10px",
                          borderRadius: "var(--E-radius-sm)",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                          {/* Locked */}
                        </div>
                      )}
                    </label>

                    {!isRewardLocked && useRewardDiscount && (
                      <div style={{ marginTop: "var(--E-space-sm)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "var(--E-space-sm)" }}>
                          <span style={{ fontSize: "0.875rem", color: "var(--E-navy)", fontWeight: 600 }}>₹</span>
                          <input
                            type="number"
                            placeholder={`Enter amount (max ₹${customRound(maxDiscountINR)})`}
                            value={rewardDiscountInput}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                setRewardDiscountInput(val);
                                setDiscountError("");
                              } else {
                                const num = parseInt(val, 10);
                                if (!isNaN(num) && num >= 0) {
                                  if (num <= customRound(maxDiscountINR)) {
                                    setRewardDiscountInput(num.toString());
                                    setDiscountError("");
                                  } else {
                                    setDiscountError(`Cannot exceed ₹${customRound(maxDiscountINR)} limit`);
                                  }
                                }
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              border: discountError ? "1.5px solid var(--E-danger)" : "1.5px solid var(--E-border)",
                              borderRadius: "var(--E-radius-md)",
                              fontSize: "0.875rem",
                            }}
                          />
                          {rewardDiscount > 0 && (
                            <div style={{ fontWeight: 700, color: "var(--E-success)", fontSize: "0.875rem", whiteSpace: "nowrap" }}>
                              -₹{customRound(rewardDiscount * 83)}
                            </div>
                          )}
                        </div>
                        {discountError && (
                          <div style={{ fontSize: "0.75rem", color: "var(--E-danger)", marginTop: 4 }}>
                            {discountError}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {payMethod === "wallet" && (
                  <div className="E-payment-form">
                    <div className="E-wallet-balance">
                      <span className="E-wallet-label">Available Reward Balance:</span>
                      <span className="E-wallet-amount">₹{customRound(walletBalance).toLocaleString("en-IN")}</span>
                    </div>
                    {userEmail && (
                      <div style={{ fontSize: "0.75rem", color: "var(--E-text-secondary)", marginTop: 4 }}>
                        <span style={{ color: "var(--E-success)" }}>₹{customRound(redeemableBalance).toLocaleString("en-IN")} redeemable</span>
                        <span style={{ margin: "0 6px" }}>|</span>
                        <span>₹{customRound(nonRedeemableBalance).toLocaleString("en-IN")} non-redeemable</span>
                      </div>
                    )}
                    {!userEmail && <p className="E-error" style={{ marginTop: 8 }}>Please login to use reward money</p>}
                  </div>
                )}

                {payMethod === "upi" && (
                  <div className="E-payment-form">
                    <label>UPI ID</label>
                    <input placeholder="yourname@upi" value={upiId} onChange={e => setUpiId(e.target.value)} />
                  </div>
                )}

                {payMethod === "netbanking" && (
                  <div className="E-payment-form">
                    <label>Select Bank</label>
                    <select value={bank} onChange={e => setBank(e.target.value)}>
                      <option value="">Choose your bank</option>
                      {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                )}

                {payMethod === "card" && (
                  <div className="E-payment-form">
                    <div className="E-form-field">
                      <label>Card Number</label>
                      <input placeholder="1234 5678 9012 3456" value={card.number} onChange={e => setCard(p => ({ ...p, number: e.target.value }))} maxLength={19} />
                    </div>
                    <div className="E-form-field">
                      <label>Name on Card</label>
                      <input placeholder="Full Name" value={card.name} onChange={e => setCard(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--E-space-md)" }}>
                      <div className="E-form-field">
                        <label>Expiry</label>
                        <input placeholder="MM/YY" value={card.expiry} onChange={e => setCard(p => ({ ...p, expiry: e.target.value }))} maxLength={5} />
                      </div>
                      <div className="E-form-field">
                        <label>CVV</label>
                        <input placeholder="CVV" type="password" value={card.cvv} onChange={e => setCard(p => ({ ...p, cvv: e.target.value }))} maxLength={4} />
                      </div>
                    </div>
                  </div>
                )}

                {error && <p className="E-error">{error}</p>}

                <div className="E-checkout-nav">
                  <button className="E-btn-ghost" onClick={() => setStep(1)}>Back</button>
                  <button
                    className="E-btn-primary"
                    onClick={() => {
                      const payErr = getPaymentError();
                      if (payErr) { setError(payErr); return; }
                      setStep(3);
                    }}
                    disabled={!canReview}
                  >
                    Review Order
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <div className="E-checkout-block" data-aos="fade-up">
                <h3 className="E-checkout-block-title">Review Your Order</h3>
                <div className="E-review-order-item">
                  <span>{item.countryName} — {item.label} ×{quantity}</span>
                  <span>{convertPrice(item.price * quantity, currency)}</span>
                </div>
                <div className="E-review-breakdown">
                  <div className="E-review-row">
                    <span>Subtotal (×{quantity})</span>
                    <span>{convertPrice(subtotalUSD, currency)}</span>
                  </div>
                  {(payMethod === "wallet" || useRewardDiscount) && appliedDiscount > 0 && (
                    <div className="E-review-row" style={{ color: "var(--E-success)" }}>
                      <span>Reward Discount Applied</span>
                      <span>-{convertPrice(appliedDiscount, currency)}</span>
                    </div>
                  )}
                  <div className="E-review-row">
                    <span>Tax (5%)</span>
                    <span>{convertPrice(taxUSD, currency)}</span>
                  </div>
                  <div className="E-review-row">
                    <span>Convenience Fee ({PAYMENT_METHODS.find(p => p.id === payMethod)?.label})</span>
                    <span>{convFeeUSD === 0 ? "Free" : convertPrice(convFeeUSD, currency)}</span>
                  </div>
                </div>
                <div className="E-review-total">
                  <span>Total</span>
                  <span>{convertPrice(finalPayable, currency)}</span>
                </div>
                <div className="E-review-billing-summary">
                  <p><strong>Delivery:</strong> {billingDetails.email}</p>
                  <p><strong>WhatsApp:</strong> {billingDetails.whatsappCode} {billingDetails.whatsapp}</p>
                  <p><strong>Payment:</strong> {PAYMENT_METHODS.find(p => p.id === payMethod)?.label}</p>
                  <p><strong>SIM Type:</strong> {simType === "esim" ? "eSIM (Digital)" : "Physical SIM"}</p>
                </div>
                <div className="E-checkout-nav">
                  <button className="E-btn-ghost" onClick={() => setStep(2)}>Back</button>
                  <button className="E-btn-primary" onClick={handlePlaceOrder}>Confirm &amp; Pay</button>
                </div>
              </div>
            )}

          </div>

          {/* Sticky order summary */}
          <div className="E-checkout-summary" data-aos="fade-left">
            <h3 className="E-summary-title">Order Summary</h3>
            <div className="E-summary-item">
              <span>{item.countryName} — {item.label} ×{quantity}</span>
              <span>{convertPrice(subtotalUSD, currency)}</span>
            </div>
            <div className="E-summary-row">
              <span>Tax (5%)</span>
              <span>{convertPrice(taxUSD, currency)}</span>
            </div>
            <div className="E-summary-row">
              <span>Convenience Fee</span>
              <span>{convFeeUSD === 0 ? "Free" : convertPrice(convFeeUSD, currency)}</span>
            </div>
            {(payMethod === "wallet" || useRewardDiscount) && appliedDiscount > 0 && (
              <div className="E-summary-row" style={{ color: "var(--E-success)" }}>
                <span>Reward Applied</span>
                <span>-{convertPrice(appliedDiscount, currency)}</span>
              </div>
            )}
            <div className="E-summary-divider"></div>
            <div className="E-summary-row E-summary-total">
              <span>Total</span>
              <span>{convertPrice(finalPayable, currency)}</span>
            </div>
            <div className="E-fee-note">
              Convenience fee varies by payment method.
            </div>
          </div>
        </div>
      </div>

      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}

      {showLoginModal && (
        <div
          className="E-login-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowLoginModal(false)}
        >
          <div className="E-login-modal-box" data-aos="zoom-in" style={{ position: "relative", maxWidth: "520px" }}>
            <button
              className="E-login-modal-close"
              onClick={() => setShowLoginModal(false)}
              style={{ position: "absolute", top: "20px", right: "20px", zIndex: 1 }}
            >
              &#215;
            </button>
            <Login isModal={true} onLoginSuccess={(user) => {
              setShowLoginModal(false);
            }} />
          </div>
        </div>
      )}
    </main>
  );
};

// ─── Root App ────────────────────────────────────────────────
export default function App() {
  const { esimState, setEsimState } = useEsim();
  const [page, setPage] = useState({ id: "home", params: {} });
  const [currency, setCurrency] = useState("INR");
  const [regions, setRegions] = useState(REGIONS);
  const [countries, setCountries] = useState(COUNTRIES);
  const [planTemplates, setPlanTemplates] = useState([]);
  const [faqItems] = useState(FAQ_ITEMS);
  const [reviews] = useState(REVIEWS);

  useEffect(() => { if (!currency || currency === "USD") setCurrency("INR"); }, []);

  // Prime connectivity — fetch eSIM catalog data from backend API, overlay on static fallback
  useEffect(() => {
    let active = true;
    Promise.all([
      esimService.listCountries().catch(() => ({ data: null })),
      esimService.listRegions().catch(() => ({ data: null })),
      esimService.listPlans().catch(() => ({ data: null })),
    ]).then(([countriesRes, regionsRes, plansRes]) => {
      if (!active) return;
      const apiCountries = countriesRes?.data;
      const apiRegions = regionsRes?.data;
      if (Array.isArray(apiCountries) && apiCountries.length > 0) {
        setCountries(apiCountries.map((c) => ({
          ...c,
          region: c.region_id,
          heroImage: c.hero_image,
          description: c.description || "",
          networks: Array.isArray(c.networks) ? c.networks : [],
          popular: c.is_popular,
          mostVisited: c.most_visited,
          topPicks: c.top_picks,
          trending: c.trending,
          bestDeal: c.best_deal,
          mostLoved: c.most_loved,
        })));
      }
      if (Array.isArray(apiRegions) && apiRegions.length > 0) {
        setRegions(apiRegions);
      }
      setPlanTemplates(Array.isArray(plansRes?.data) ? plansRes.data : []);
    });
    return () => { active = false; };
  }, []);

  const { cart } = esimState;
  const setCart = (val) => setEsimState(prev => ({ ...prev, cart: typeof val === 'function' ? val(prev.cart) : val }));

  const navigate = (id, params = {}) => {
    setPage({ id, params });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addToCart = (plan) => {
    setCart(prev => {
      if (prev.find(item => item.id === plan.id)) return prev;
      return [...prev, plan];
    });
  };

  useLayoutEffect(() => {
    const handleEsimNav = (e) => {
      const { id, params } = e.detail || {};
      if (id) {
        setPage({ id, params: params || {} });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    window.addEventListener('esimNavigate', handleEsimNav);
    return () => window.removeEventListener('esimNavigate', handleEsimNav);
  }, []);

  useLayoutEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add("E-aos-animate");
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -30px 0px" });
    const elements = document.querySelectorAll("[data-aos]");
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [page]);

  return (
    <div className="E-app">
      <FloatingActions cart={cart} navigate={navigate} />
      {page.id === "home" && <HomePage navigate={navigate} currency={currency} countries={countries} regions={regions} faqItems={faqItems} reviews={reviews} />}
      {page.id === "country" && (
        <CountryPage countryId={page.params.countryId} navigate={navigate} addToCart={addToCart} currency={currency} countries={countries} planTemplates={planTemplates} faqItems={faqItems} />
      )}
      {page.id === "cart" && <CartPage cart={cart} setCart={setCart} navigate={navigate} currency={currency} />}
      {page.id === "checkout" && (
        <CheckoutPage item={page.params.item} setCart={setCart} navigate={navigate} currency={currency} />
      )}
      {page.id === "faq" && <FAQSection faqItems={faqItems} />}
    </div>
  );
}
